import React, { useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { api, formatApiErrorDetail, fmtINR, today } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Btn, Field, Input, Select, Combobox, Empty } from "@/components/UI";
import { toast } from "sonner";

const STAGE_META = {
  knitting:   { title: "Knitting",   partyType: "knitter",   unit: "Kg",  needsProduct: false },
  dyeing:     { title: "Dyeing",     partyType: "dyer",      unit: "Kg",  needsProduct: false },
  compacting: { title: "Compacting", partyType: "compactor", unit: "Kg",  needsProduct: false },
  printing:   { title: "Printing",   partyType: "printer",   unit: "Kg",  needsProduct: false },
  stitching:  { title: "Stitching",  partyType: "stitcher",  unit: "Doz", needsProduct: true  },
};

const STATUS_STYLE = {
  pending:  "bg-yellow-100 text-yellow-800 border-yellow-300",
  partial:  "bg-blue-100 text-blue-800 border-blue-300",
  received: "bg-green-100 text-green-800 border-green-300",
};

const emptyLine = (needsProduct) => ({
  product_id: needsProduct ? "" : null,
  hsn: "",
  lot_no: "",
  qty_sent: "",
  rate: "",
  notes: "",
});

// Which downstream stages a given DC stage can auto-forward to
const NEXT_STAGE_MAP = {
  knitting:   [{ value: "dyeing",     label: "Dyeing",     partyType: "dyer" },
               { value: "bleaching",  label: "Bleaching",  partyType: "dyer" }],
  dyeing:     [{ value: "compacting", label: "Compacting", partyType: "compactor" },
               { value: "printing",   label: "Printing",   partyType: "printer" }],
  compacting: [{ value: "printing",   label: "Printing",   partyType: "printer" }],
  printing:   [{ value: "compacting", label: "Compacting", partyType: "compactor" }],
  stitching:  [],
};

export default function DCPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const location = useLocation();
  const initialStage = (() => {
    const s = new URLSearchParams(location.search).get("stage");
    return STAGE_META[s] ? s : "knitting";
  })();
  const [stage, setStage] = useState(initialStage);
  const meta = STAGE_META[stage];

  const [list, setList] = useState([]);
  const [parties, setParties] = useState([]);
  const [products, setProducts] = useState([]);
  const [pool, setPool] = useState(null);
  const [company, setCompany] = useState({ company_name: "SLKC", tagline: "Selvalakshmi Knits & Co.", address: "", gst_no: "", phone: "" });
  const [suggestedNumber, setSuggestedNumber] = useState("");

  const [form, setForm] = useState({ dc_number: "", date: today(), party_id: "", notes: "", next_stage: "", next_party_id: "", lines: [emptyLine(false)] });
  const [openDcId, setOpenDcId] = useState(null);
  const [receiveDraft, setReceiveDraft] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [q, setQ] = useState("");
  const [dyeSubtype, setDyeSubtype] = useState("all"); // all | dye | bleach — only used on stage=dyeing
  const [nextPrompt, setNextPrompt] = useState(null); // { dc, defaultStage, defaultPartyId }

  const isBleachDc = (dc) => /bleach/i.test(dc.notes || "");
  const filteredList = useMemo(() => {
    let arr = list;
    if (stage === "dyeing" && dyeSubtype !== "all") {
      arr = arr.filter((dc) => dyeSubtype === "bleach" ? isBleachDc(dc) : !isBleachDc(dc));
    }
    if (!q.trim()) return arr;
    const needle = q.trim().toLowerCase();
    return arr.filter((dc) => {
      const hay = `${dc.dc_number || ""} ${dc.party_name || ""} ${dc.notes || ""} ${(dc.lines || []).map((l) => `${l.product_label || ""} ${l.notes || ""} ${l.lot_no || ""}`).join(" ")}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [list, q, stage, dyeSubtype]);

  const loadPool = () => api.get("/fabric-pool").then((r) => setPool(r.data)).catch(() => {});
  const loadSuggested = () => api.get("/dcs/next-number").then((r) => setSuggestedNumber(r.data.next || "")).catch(() => {});
  const load = () => {
    api.get(`/dcs?stage=${stage}`).then((r) => setList(r.data));
    loadPool();
    loadSuggested();
  };
  useEffect(() => {
    load();
    setForm({ dc_number: "", date: today(), party_id: "", notes: "", next_stage: "", next_party_id: "", lines: [emptyLine(meta.needsProduct)] });
    setOpenDcId(null);
    setEditingId(null);
    // eslint-disable-next-line
  }, [stage]);
  useEffect(() => {
    const s = new URLSearchParams(location.search).get("stage");
    if (STAGE_META[s] && s !== stage) setStage(s);
    // eslint-disable-next-line
  }, [location.search]);
  useEffect(() => {
    api.get("/parties").then((r) => setParties(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/settings/company").then((r) => setCompany(r.data)).catch(() => {});
  }, []);

  const filteredParties = useMemo(
    () => parties.filter((p) => !p.type || p.type === meta.partyType),
    [parties, meta.partyType]
  );

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine(meta.needsProduct)] }));
  const rmLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const setLine = (i, patch) =>
    setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) }));

  const submitNew = async (e) => {
    e.preventDefault();
    try {
      if (!form.party_id) { toast.error("Select party"); return; }
      const isStitch = stage === "stitching";
      const lines = form.lines
        .filter((l) => Number(l.qty_sent) > 0)
        .map((l) => {
          // Stitching qty_sent is a "dozen.pcs" hybrid — parse the raw string so
          // 15.11 becomes 15 doz + 11 pcs = 191 pcs (NOT 15.11×12=181.32).
          // Max decimal part is 11 (since 12 pcs = 1 dozen).
          let qsp = null;
          if (isStitch) {
            const raw = String(l.qty_sent).trim();
            const [dozStr, pcsStr = "0"] = raw.split(".");
            const dozInt = parseInt(dozStr, 10) || 0;
            const pcsInt = parseInt(pcsStr, 10) || 0;
            if (pcsInt > 11) throw new Error(`Line ${dozInt}.${pcsStr}: fractional part must be 0–11 pcs (12 pcs = 1 dozen)`);
            qsp = dozInt * 12 + pcsInt;
          }
          return {
            product_id: meta.needsProduct ? l.product_id || null : null,
            hsn: (l.hsn || "").trim(),
            lot_no: (l.lot_no || "").trim(),
            qty_sent: Number(l.qty_sent),
            ...(qsp !== null ? { qty_sent_pcs: qsp } : {}),
            rate: Number(l.rate || 0),
            notes: l.notes || "",
          };
        });
      if (lines.length === 0) { toast.error("Add at least one line"); return; }
      if (meta.needsProduct && lines.some((l) => !l.product_id)) { toast.error("Select product for each line"); return; }

      if (editingId) {
        if (!window.confirm("Editing a DC will RESET all received quantities to 0 and clear the linked ledger entry. Continue?")) return;
        await api.put(`/dcs/${editingId}`, {
          dc_number: form.dc_number || suggestedNumber || `DC-${Date.now()}`,
          date: form.date,
          stage,
          party_id: form.party_id,
          notes: form.notes || "",
          next_stage: form.next_stage || "",
          next_party_id: form.next_party_id || "",
          lines,
        });
        toast.success("DC updated · received qty reset");
      } else {
        await api.post("/dcs", {
          dc_number: form.dc_number || suggestedNumber || `DC-${Date.now()}`,
          date: form.date,
          stage,
          party_id: form.party_id,
          notes: form.notes || "",
          next_stage: form.next_stage || "",
          next_party_id: form.next_party_id || "",
          lines,
        });
        toast.success("DC created");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const resetForm = () => {
    setForm({ dc_number: "", date: today(), party_id: "", notes: "", next_stage: "", next_party_id: "", lines: [emptyLine(meta.needsProduct)] });
    setEditingId(null);
  };

  const startEdit = (dc) => {
    setEditingId(dc.id);
    setForm({
      dc_number: dc.dc_number,
      date: dc.date,
      party_id: dc.party_id,
      notes: dc.notes || "",
      next_stage: dc.next_stage || "",
      next_party_id: dc.next_party_id || "",
      lines: (dc.lines || []).map((l) => ({
        product_id: l.product_id || (meta.needsProduct ? "" : null),
        hsn: l.hsn || "",
        lot_no: l.lot_no || "",
        qty_sent: l.qty_sent,
        rate: l.rate,
        notes: l.notes || "",
      })),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleOpen = (dc) => {
    if (openDcId === dc.id) { setOpenDcId(null); return; }
    setOpenDcId(dc.id);
    // seed draft with current received values
    const d = {};
    (dc.lines || []).forEach((l) => {
      if (dc.stage === "stitching") {
        d[l.id] = {
          boxes_received: String(l.boxes_received || 0),
          loose_pcs_received: String(l.loose_pcs_received || 0),
          mistake_pcs: String(l.mistake_pcs || 0),
        };
      } else {
        d[l.id] = String(l.qty_received || 0);
      }
    });
    setReceiveDraft(d);
  };

  const submitReceive = async (dc) => {
    try {
      const isStitch = dc.stage === "stitching";
      const receives = (dc.lines || []).map((l) => {
        if (isStitch) {
          const d = receiveDraft[l.id] || {};
          return {
            line_id: l.id,
            boxes_received: Number(d.boxes_received ?? l.boxes_received ?? 0) || 0,
            loose_pcs_received: Number(d.loose_pcs_received ?? l.loose_pcs_received ?? 0) || 0,
            mistake_pcs: Number(d.mistake_pcs ?? l.mistake_pcs ?? 0) || 0,
          };
        }
        return { line_id: l.id, qty_received: Number(receiveDraft[l.id] || 0) };
      });
      const { data: updated } = await api.put(`/dcs/${dc.id}/receive`, { receives });
      toast.success("Receive updated · ledger auto-synced");
      load();
      setOpenDcId(null);
      // Post-receive: if this stage has downstream stages, prompt to auto-create next DC.
      const nextOptions = NEXT_STAGE_MAP[dc.stage] || [];
      const hasReceived = (updated.total_received || 0) > 0;
      if (hasReceived && nextOptions.length > 0) {
        // Pre-select next-vendor: use DC's saved next_party_id first, else fall back to
        // company-settings default_compactor_id when the next stage is compacting.
        let defaultPartyId = dc.next_party_id || "";
        const defaultStage = dc.next_stage || nextOptions[0].value;
        if (!defaultPartyId && defaultStage === "compacting" && company?.default_compactor_id) {
          defaultPartyId = company.default_compactor_id;
        }
        setNextPrompt({ dc: updated, defaultStage, defaultPartyId });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const createNextDc = async ({ dcId, stage: nextStage, partyId }) => {
    try {
      const { data } = await api.post(`/dcs/${dcId}/create-next`, {
        stage: nextStage,
        party_id: partyId,
      });
      toast.success(`Auto ${data.stage} DC ${data.dc_number} created`);
      setNextPrompt(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const markFullyReceived = async (dc) => {
    if (!window.confirm(`Mark this DC as FULLY received?\n\nAll pending qty will be receipted at once. You can still edit individual lines afterward.`)) return;
    try {
      const isStitch = dc.stage === "stitching";
      // Fetch product ppb map for stitching (to split qty_sent between boxes+loose).
      let ppbMap = {};
      if (isStitch) {
        const { data: prods } = await api.get("/products");
        prods.forEach((p) => { ppbMap[p.id] = Number(p.pieces_per_box || 0); });
      }
      const receives = (dc.lines || []).map((l) => {
        if (isStitch) {
          // Use exact integer pcs from qty_sent_pcs (dozen.pcs hybrid) — fall back
          // to legacy qty_sent*12 for older records without the field.
          const pcs = Number(l.qty_sent_pcs) > 0
            ? parseInt(l.qty_sent_pcs, 10)
            : Math.round(Number(l.qty_sent || 0) * 12);
          const ppb = ppbMap[l.product_id] || 0;
          const mistake = Number(l.mistake_pcs || 0);
          const good = Math.max(0, pcs - mistake);
          const boxes = ppb > 0 ? Math.floor(good / ppb) : 0;
          const loose = good - (boxes * ppb);
          return { line_id: l.id, boxes_received: boxes, loose_pcs_received: loose, mistake_pcs: mistake };
        }
        // KDC: single number, receive == sent
        return { line_id: l.id, qty_received: Number(l.qty_sent || 0) };
      });
      await api.put(`/dcs/${dc.id}/receive`, { receives });
      toast.success("Marked fully received · ledger auto-synced");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this DC? Ledger entry will be reversed.")) return;
    await api.delete(`/dcs/${id}`);
    load();
  };

  const whatsappDC = (dc) => {
    const party = parties.find((p) => p.id === dc.party_id);
    const phone = (party?.phone || "").replace(/[^\d]/g, "");
    const linesTxt = (dc.lines || [])
      .map((l, i) => {
        const p = l.product_label ? ` ${l.product_label}` : "";
        const h = l.hsn ? ` [HSN ${l.hsn}]` : "";
        return `${i + 1}.${p}${h} ${l.qty_sent} ${meta.unit}${l.rate ? ` @ ₹${l.rate}` : ""}${l.notes ? ` (${l.notes})` : ""}`;
      })
      .join("\n");
    const totalSent = (dc.lines || []).reduce((s, l) => s + Number(l.qty_sent || 0), 0);
    const totalRecv = dc.total_received || 0;
    const msg =
`*SLKC — Delivery Challan*
DC #: *${dc.dc_number}*
Date: ${dc.date}
Stage: ${meta.title} (${meta.unit})
Party: ${dc.party_name || party?.name || "—"}

${linesTxt || "(no lines)"}

Total Sent: *${totalSent} ${meta.unit}*
Received so far: ${totalRecv} ${stage === "stitching" ? "Pcs" : meta.unit}
Status: ${(dc.status || "").toUpperCase()}

Kindly confirm receipt.
— SLKC (Selvalakshmi Knits & Co.)`;
    const encoded = encodeURIComponent(msg);
    const url = phone
      ? `https://wa.me/${phone}?text=${encoded}`
      : `https://wa.me/?text=${encoded}`;
    if (!phone) {
      toast.warning("No phone on file for this party — opening WhatsApp with the message. Pick the contact manually.");
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const printDC = (dc) => {
    const rows = (dc.lines || []).map((l, i) => `
      <tr>
        <td class="c">${i + 1}</td>
        <td>${l.product_label || "—"}</td>
        <td class="c">${l.hsn || "—"}</td>
        <td class="r">${l.qty_sent}</td>
        <td class="r">${l.notes ? String(l.notes).replace(/</g, "&lt;") : ""}</td>
        <td class="r bd">${"".padEnd(8, "\u00A0")}</td>
      </tr>`).join("");
    const totalSent = (dc.lines || []).reduce((s, l) => s + Number(l.qty_sent || 0), 0);
    const html = `<!doctype html><html><head><meta charset="utf-8"/>
      <title>Challan ${dc.dc_number}</title>
      <style>
        @page { size: A4; margin: 14mm; }
        * { box-sizing: border-box; }
        body { font: 12px "Helvetica Neue", Arial, sans-serif; color: #111; margin: 0; }
        .head { display: flex; align-items: flex-start; justify-content: space-between; border-bottom: 2px solid #111; padding-bottom: 10px; }
        .brand { font-weight: 900; letter-spacing: 0.05em; font-size: 22px; }
        .brand small { display: block; font-size: 10px; letter-spacing: 0.2em; color: #555; margin-top: 4px; font-weight: 500; }
        .brand .addr { display:block; font-weight: 400; font-size: 10px; color:#444; margin-top:4px; letter-spacing: normal; max-width:280px; line-height:1.4; }
        .brand .gst { display:block; font-size:10px; margin-top:4px; font-weight:700; letter-spacing:0.1em; }
        .doc { text-align: right; }
        .doc .title { font-size: 14px; font-weight: 800; letter-spacing: 0.2em; text-transform: uppercase; }
        .doc .num { font-size: 22px; font-weight: 900; margin-top: 2px; }
        .doc .jw  { display:inline-block; margin-top:8px; padding:4px 10px; border:1.5px solid #b91c1c; color:#b91c1c; font-weight:800; letter-spacing:0.18em; font-size:10px; text-transform:uppercase; }
        .parties { display:grid; grid-template-columns: 1fr 1fr; gap:14px; margin-top:16px; }
        .parties .box { border:1px solid #ddd; padding:10px 12px; }
        .parties .lab { text-transform: uppercase; letter-spacing: 0.2em; font-size: 8.5px; color: #666; margin-bottom: 3px; }
        .parties .val { font-weight: 800; font-size: 13px; }
        .parties .sub { font-size: 10.5px; color:#333; margin-top:3px; line-height:1.35; }
        .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 12px; padding: 10px 0; border-top: 1px dashed #ccc; border-bottom: 1px solid #ddd; }
        .meta .lab { text-transform: uppercase; letter-spacing: 0.2em; font-size: 9px; color: #666; margin-bottom: 2px; }
        .meta .val { font-weight: 700; font-size: 13px; }
        table { width: 100%; border-collapse: collapse; margin-top: 14px; }
        th { text-align: left; text-transform: uppercase; letter-spacing: 0.15em; font-size: 9px; padding: 8px 6px; border-bottom: 2px solid #111; }
        td { padding: 10px 6px; border-bottom: 1px dashed #ccc; vertical-align: top; }
        .r { text-align: right; font-variant-numeric: tabular-nums; }
        .c { text-align: center; width: 28px; }
        .bd { border-left: 1px dashed #999; min-width: 90px; }
        tfoot td { border-top: 2px solid #111; font-weight: 800; }
        .sign { margin-top: 60px; display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
        .sign .box { border-top: 1px solid #111; padding-top: 6px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.2em; color: #555; }
        .note { margin-top: 14px; font-size: 11px; color: #333; white-space: pre-wrap; }
        .foot { margin-top: 30px; font-size: 9px; color: #888; text-align: center; letter-spacing: 0.2em; text-transform: uppercase; }
      </style></head><body>
      <div class="head">
        <div class="brand">
          ${company.company_name || "SLKC"}<small>${company.tagline || "Selvalakshmi · Knits &amp; Co."}</small>
          ${company.address ? `<span class="addr">${String(company.address).replace(/</g, "&lt;")}</span>` : ""}
          ${company.gst_no ? `<span class="gst">GSTIN: ${company.gst_no}</span>` : ""}
          ${company.phone ? `<span class="gst" style="font-weight:400">Ph: ${company.phone}</span>` : ""}
        </div>
        <div class="doc">
          <div class="title">Delivery Challan</div>
          <div class="num">${dc.dc_number}</div>
          <div class="jw">For Job Work Only · Not for Sale</div>
        </div>
      </div>
      <div class="parties">
        <div class="box">
          <div class="lab">From (Consignor)</div>
          <div class="val">${company.company_name || "SLKC"}</div>
          ${company.address ? `<div class="sub">${String(company.address).replace(/</g, "&lt;")}</div>` : ""}
          ${company.gst_no ? `<div class="sub"><b>GSTIN:</b> ${company.gst_no}</div>` : ""}
          ${company.phone ? `<div class="sub"><b>Ph:</b> ${company.phone}</div>` : ""}
        </div>
        <div class="box">
          <div class="lab">To (Consignee · Vendor)</div>
          <div class="val">${dc.party_name || "—"}</div>
          ${dc.party_address ? `<div class="sub">${String(dc.party_address).replace(/</g, "&lt;")}</div>` : ""}
          ${dc.party_gst_no ? `<div class="sub"><b>GSTIN:</b> ${dc.party_gst_no}</div>` : `<div class="sub" style="color:#888">GSTIN: —</div>`}
          ${dc.party_phone ? `<div class="sub"><b>Ph:</b> ${dc.party_phone}</div>` : ""}
        </div>
      </div>
      <div class="meta">
        <div><div class="lab">Date</div><div class="val">${dc.date}</div></div>
        <div><div class="lab">Stage</div><div class="val">${meta.title} · ${meta.unit}</div></div>
        <div><div class="lab">Purpose</div><div class="val">Job Work — Return Expected</div></div>
        <div><div class="lab">DC Status</div><div class="val">${(dc.status || "").toUpperCase()}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>#</th><th>Item / Product</th>
          <th class="c">HSN</th>
          <th class="r">Sent ${meta.unit}</th>
          <th class="r">Notes</th>
          <th class="r">Received ${meta.unit}</th>
        </tr></thead>
        <tbody>${rows || `<tr><td colspan="6" style="text-align:center;color:#888;padding:20px">No lines</td></tr>`}</tbody>
        <tfoot><tr>
          <td colspan="3">TOTAL</td>
          <td class="r">${totalSent}</td>
          <td colspan="1"></td>
          <td class="r bd"></td>
        </tr></tfoot>
      </table>
      ${dc.notes ? `<div class="note"><b>Notes:</b> ${String(dc.notes).replace(/</g, "&lt;")}</div>` : ""}
      <div class="note" style="margin-top:18px; padding:8px 10px; background:#fff7ed; border:1px dashed #c2410c; color:#9a3412; font-weight:600;">
        This challan is issued <b>strictly for job work</b> under Rule 55 of CGST Rules. Goods are <b>not for sale</b> and must be returned after the specified processing. Loss / damage during processing is the vendor's responsibility.
      </div>
      <div class="sign">
        <div class="box">Issued by (SLKC)</div>
        <div class="box">Received by (Vendor · Signature &amp; Date)</div>
      </div>
      <div class="foot">Computer-generated · SLKC Manufacturing OS</div>
      <script>window.onload = () => { window.print(); };</script>
      </body></html>`;
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) { toast.error("Pop-up blocked. Allow pop-ups to print."); return; }
    w.document.write(html);
    w.document.close();
  };

  const reconcileLegacyPartial = async () => {
    if (!window.confirm(
      `RECONCILE legacy Stitching DCs stuck in "partial" due to old decimal-quirk data?\n\n` +
      `For every stitching DC line with no dozen.pcs hybrid value AND some qty received, ` +
      `this will set qty_sent = qty_received (i.e., close it out with what came back).\n\n` +
      `Lines that have never been received are left alone. Admin-only. Continue?`
    )) return;
    try {
      const { data } = await api.post("/dcs/admin/reconcile-legacy-partial-stitching");
      toast.success(`Reconciled ${data.dcs_fixed} DC${data.dcs_fixed === 1 ? "" : "s"} · ${data.lines_fixed} line${data.lines_fixed === 1 ? "" : "s"} closed`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader label="Outsourced" title="Delivery Challan (DC)" subtitle="One DC → many products / lots. Enter exact received qty per line." />

      <div className="px-4 md:px-8 pt-4 flex flex-wrap gap-2 items-center">
        {Object.keys(STAGE_META).map((s) => (
          <button
            key={s}
            onClick={() => setStage(s)}
            data-testid={`dc-stage-${s}`}
            className={`h-9 px-4 font-mono text-[11px] tracking-[0.15em] uppercase font-bold ${stage === s ? "bg-foreground text-background" : "bg-white border border-border"}`}
          >
            {STAGE_META[s].title} · {STAGE_META[s].unit}
          </button>
        ))}
        {isAdmin && stage === "stitching" && (
          <button
            type="button"
            onClick={reconcileLegacyPartial}
            data-testid="dc-reconcile-legacy-partial"
            title="One-time cleanup for old stitching DCs stuck in partial due to decimal quirks"
            className="ml-auto h-9 px-3 font-mono text-[10px] tracking-[0.15em] uppercase font-bold bg-amber-50 border border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white"
          >
            ⚙ Reconcile Legacy Partial
          </button>
        )}
      </div>

      {pool && (
        <div className="px-4 md:px-8 pt-4" data-testid="fabric-pool">
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            <div className="border border-border p-3 bg-white">
              <div className="label-caps text-muted-foreground">Grey Fabric Pool</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-2xl font-black tabular" data-testid="pool-grey">{pool.grey_kg}</span>
                <span className="text-xs text-muted-foreground">Kg</span>
              </div>
              <div className="mt-1 text-[10px] font-mono text-muted-foreground">
                knit {pool.totals.knitting_received} − dye-in {pool.totals.dyeing_consumed}
              </div>
            </div>
            <div className="border border-border p-3 bg-white">
              <div className="label-caps text-muted-foreground">Dyed Fabric Pool</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-2xl font-black tabular" data-testid="pool-dyed">{pool.dyed_kg}</span>
                <span className="text-xs text-muted-foreground">Kg</span>
              </div>
              <div className="mt-1 text-[10px] font-mono text-muted-foreground">
                dye {pool.totals.dyeing_received} − comp-in {pool.totals.compacting_consumed}
              </div>
            </div>
            <div className="border border-border p-3 bg-white">
              <div className="label-caps text-muted-foreground">Finished Fabric Pool</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-2xl font-black tabular" data-testid="pool-finished">{pool.finished_kg}</span>
                <span className="text-xs text-muted-foreground">Kg</span>
              </div>
              <div className="mt-1 text-[10px] font-mono text-muted-foreground">
                comp {pool.totals.compacting_received} − cut {pool.totals.cutting_consumed}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="p-4 md:p-8 grid grid-cols-1 xl:grid-cols-5 gap-3 md:gap-4">
        <Card title={editingId ? `Edit DC · ${meta.title}` : `New DC · ${meta.title}`} className="xl:col-span-2">
          <form onSubmit={submitNew} className="space-y-3" data-testid="dc-form">
            {editingId && (
              <div className="bg-amber-50 border border-amber-300 p-2 text-[11px] font-mono">
                ⚠ Editing this DC will RESET all received quantities and clear its ledger entry.
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Field label="DC #">
                <div className="flex gap-1">
                  <Input data-testid="dc-number" placeholder={suggestedNumber || "Auto if blank"} value={form.dc_number} onChange={(e) => setForm({ ...form, dc_number: e.target.value })} />
                  {!editingId && suggestedNumber && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, dc_number: suggestedNumber })}
                      data-testid="dc-number-use-suggested"
                      title={`Use ${suggestedNumber}`}
                      className="h-9 px-2 border border-border bg-white font-mono text-[10px] tracking-[0.1em] uppercase font-bold hover:bg-secondary whitespace-nowrap"
                    >
                      Use {suggestedNumber.split("/").pop()}
                    </button>
                  )}
                </div>
              </Field>
              <Field label="Date"><Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
            </div>
            <Field label={`Party (${meta.partyType})`}>
              <Combobox
                testid="dc-party"
                required
                value={form.party_id}
                onChange={(v) => setForm({ ...form, party_id: v })}
                placeholder={`Type to search ${meta.partyType}…`}
                options={filteredParties.map((p) => ({ value: p.id, label: p.name, hint: p.phone || "" }))}
              />
            </Field>
            <div className="border border-border p-3 bg-secondary space-y-2">
              <div className="flex items-center justify-between">
                <span className="label-caps">Lines · {meta.unit}</span>
                <button type="button" onClick={addLine} data-testid="dc-add-line" className="label-caps hover:text-foreground">+ Add line</button>
              </div>
              {form.lines.map((ln, i) => (
                <div key={i} className="grid grid-cols-12 gap-1 items-start bg-white p-2 border border-border">
                  {meta.needsProduct && (
                    <div className="col-span-12">
                      <Combobox
                        testid={`dc-line-product-${i}`}
                        required
                        value={ln.product_id || ""}
                        onChange={(pid) => {
                          const prod = products.find((p) => p.id === pid);
                          setLine(i, { product_id: pid, hsn: (!ln.hsn && prod?.hsn) ? prod.hsn : ln.hsn });
                        }}
                        placeholder="Type to search product / SKU / size…"
                        options={products.map((p) => ({
                          value: p.id,
                          label: `${p.style_name} / ${p.sku} / ${p.size} / ${p.color}`,
                          hint: p.hsn ? `HSN ${p.hsn}` : "",
                        }))}
                      />
                    </div>
                  )}
                  <div className="col-span-3">
                    <Input
                      data-testid={`dc-line-qty-${i}`}
                      type={stage === "stitching" ? "text" : "number"}
                      step="0.01"
                      inputMode="decimal"
                      pattern={stage === "stitching" ? "^\\d+(\\.\\d{1,2})?$" : undefined}
                      placeholder={stage === "stitching" ? "Sent 10.11 (doz.pcs)" : `Sent ${meta.unit}`}
                      required
                      value={ln.qty_sent}
                      onChange={(e) => setLine(i, { qty_sent: e.target.value })}
                    />
                    {stage === "stitching" && ln.qty_sent && (() => {
                      const raw = String(ln.qty_sent).trim();
                      const [dozStr, pcsStr = "0"] = raw.split(".");
                      const dozInt = parseInt(dozStr, 10) || 0;
                      const pcsInt = parseInt(pcsStr, 10) || 0;
                      const bad = pcsInt > 11;
                      return (
                        <div className={`text-[10px] font-mono mt-0.5 ${bad ? "text-destructive" : "text-muted-foreground"}`}>
                          {bad ? `⚠ max .11 (12 pcs = 1 dozen)` : `= ${dozInt} doz${pcsInt ? ` + ${pcsInt} pcs` : ""} = ${dozInt * 12 + pcsInt} pcs`}
                        </div>
                      );
                    })()}
                  </div>
                  {isAdmin && <div className="col-span-2"><Input type="number" step="0.01" placeholder="Rate ₹" value={ln.rate} onChange={(e) => setLine(i, { rate: e.target.value })} /></div>}
                  <div className={isAdmin ? "col-span-2" : "col-span-3"}><Input data-testid={`dc-line-lot-${i}`} placeholder="Lot #" value={ln.lot_no} onChange={(e) => setLine(i, { lot_no: e.target.value.toUpperCase() })} /></div>
                  <div className="col-span-2"><Input data-testid={`dc-line-hsn-${i}`} placeholder="HSN" value={ln.hsn} onChange={(e) => setLine(i, { hsn: e.target.value.replace(/[^\d]/g, "").slice(0, 8) })} /></div>
                  <div className={isAdmin ? "col-span-2" : "col-span-3"}><Input placeholder="Notes" value={ln.notes} onChange={(e) => setLine(i, { notes: e.target.value })} /></div>
                  <div className="col-span-1 text-right">
                    {form.lines.length > 1 && (
                      <button type="button" onClick={() => rmLine(i)} className="text-destructive font-mono text-xs h-9 px-1">×</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {(NEXT_STAGE_MAP[stage] || []).length > 0 && (
              <div className="border border-dashed border-border p-3 bg-white space-y-2" data-testid="dc-next-stage-block">
                <div className="label-caps">Auto-forward on Receive (optional)</div>
                <div className="text-xs text-muted-foreground">
                  When lines are received, we'll offer to auto-create the next DC. You can still change these at receive time.
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Next Stage">
                    <Select data-testid="dc-next-stage" value={form.next_stage} onChange={(e) => setForm({ ...form, next_stage: e.target.value, next_party_id: "" })}>
                      <option value="">— none —</option>
                      {NEXT_STAGE_MAP[stage].map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </Select>
                  </Field>
                  <Field label="Next Vendor">
                    <Combobox
                      testid="dc-next-party"
                      value={form.next_party_id}
                      onChange={(v) => setForm({ ...form, next_party_id: v })}
                      disabled={!form.next_stage}
                      placeholder={form.next_stage ? "Type to search vendor…" : "Pick a stage first"}
                      options={(() => {
                        const opt = NEXT_STAGE_MAP[stage].find((o) => o.value === form.next_stage);
                        if (!opt) return [];
                        return parties.filter((p) => p.type === opt.partyType).map((p) => ({ value: p.id, label: p.name, hint: p.phone || "" }));
                      })()}
                    />
                  </Field>
                </div>
              </div>
            )}
            <Field label="Notes"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            <div className="flex gap-2">
              <Btn data-testid="dc-submit">{editingId ? "Update DC" : "Save DC"}</Btn>
              {editingId && (
                <button type="button" onClick={resetForm} data-testid="dc-cancel-edit" className="h-9 px-4 border border-border font-mono text-[11px] tracking-[0.15em] uppercase font-bold">
                  Cancel
                </button>
              )}
            </div>
          </form>
        </Card>

        <Card
          title={`${meta.title} DCs · ${filteredList.length}${q ? ` (filtered from ${list.length})` : ""}`}
          right={
            <div className="flex items-center gap-2 flex-wrap">
              {stage === "dyeing" && (
                <div className="flex gap-1" data-testid="dc-dye-subtype-chips">
                  {[{k:"all",l:"All"},{k:"dye",l:"Dye"},{k:"bleach",l:"Bleach"}].map((c) => (
                    <button
                      key={c.k}
                      onClick={() => setDyeSubtype(c.k)}
                      data-testid={`dc-dye-subtype-${c.k}`}
                      className={`h-8 px-3 font-mono text-[10px] tracking-[0.15em] uppercase font-bold ${dyeSubtype === c.k ? "bg-foreground text-background" : "bg-white border border-border"}`}
                    >
                      {c.l}
                    </button>
                  ))}
                </div>
              )}
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search vendor / DC# / lot #…"
                className="h-8 px-2 bg-white border border-border font-mono text-xs w-56"
                data-testid="dc-search"
              />
            </div>
          }
          className="xl:col-span-3"
        >
          <div className="table-scroll hidden md:block" data-testid="dc-scroll">
            <table className="table-dense w-full">
              <thead>
                <tr>
                  <th>DC #</th><th>Date</th><th>Party</th>
                  <th className="text-right">Sent {meta.unit}</th>
                  <th className="text-right">Recv {stage === "stitching" ? "Pcs" : meta.unit}</th>
                  {isAdmin && <th className="text-right">Bill</th>}
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((dc) => (
                  <React.Fragment key={dc.id}>
                    <tr>
                      <td className="font-mono font-semibold">
                        <div>{dc.dc_number}</div>
                        {dc.is_auto && <span className="text-[9px] tracking-widest font-bold text-accent">AUTO</span>}
                        {(() => {
                          const lots = [...new Set((dc.lines || []).map((l) => l.lot_no).filter(Boolean))];
                          return lots.length > 0 ? (
                            <div className="text-[10px] text-muted-foreground font-normal">
                              Lot {lots.map((lot, i) => (
                                <React.Fragment key={lot}>
                                  {i > 0 && ", "}
                                  <Link to={`/lots/${encodeURIComponent(lot)}`} className="hover:underline text-accent">{lot}</Link>
                                </React.Fragment>
                              ))}
                            </div>
                          ) : null;
                        })()}
                      </td>
                      <td className="font-mono">{dc.date}</td>
                      <td>
                        <div>{dc.party_name}</div>
                        {dc.next_stage && (
                          <div className="text-[10px] text-accent">→ {dc.next_stage}{dc.next_party_name ? ` · ${dc.next_party_name}` : ""}</div>
                        )}
                      </td>
                      <td className="tabular text-right">{dc.total_sent}</td>
                      <td className="tabular text-right font-bold">{dc.total_received}</td>
                      {isAdmin && <td className="tabular text-right">{fmtINR(dc.total_cost || 0)}</td>}
                      <td>
                        <span className={`inline-block label-caps px-2 py-0.5 border ${STATUS_STYLE[dc.status]}`}>{dc.status}</span>
                      </td>
                      <td className="text-right whitespace-nowrap">
                        <button onClick={() => toggleOpen(dc)} data-testid={`dc-open-${dc.id}`} className="label-caps hover:text-foreground mr-3">
                          {openDcId === dc.id ? "Close" : "Receive"}
                        </button>
                        {dc.status !== "received" && (
                          <button
                            onClick={() => markFullyReceived(dc)}
                            data-testid={`dc-mark-received-${dc.id}`}
                            className="label-caps text-green-700 hover:text-green-900 mr-3"
                            title="Mark this DC fully received in one tap"
                          >
                            ✓ Mark Received
                          </button>
                        )}
                        <button onClick={() => printDC(dc)} data-testid={`dc-print-${dc.id}`} className="label-caps hover:text-foreground mr-3">Print</button>
                        <button onClick={() => whatsappDC(dc)} data-testid={`dc-whatsapp-${dc.id}`} className="label-caps text-green-700 hover:text-green-900 mr-3">WhatsApp</button>
                        {(NEXT_STAGE_MAP[dc.stage] || []).length > 0 && (dc.total_received || 0) > 0 && (
                          <button
                            onClick={() => setNextPrompt({
                              dc,
                              defaultStage: dc.next_stage || (NEXT_STAGE_MAP[dc.stage][0]?.value || ""),
                              defaultPartyId: dc.next_party_id
                                || ((dc.next_stage || NEXT_STAGE_MAP[dc.stage][0]?.value) === "compacting" && company?.default_compactor_id ? company.default_compactor_id : ""),
                            })}
                            data-testid={`dc-forward-${dc.id}`}
                            className="label-caps text-accent hover:underline mr-3"
                          >
                            Forward →
                          </button>
                        )}
                        {isAdmin && (
                          <>
                            <button onClick={() => startEdit(dc)} data-testid={`dc-edit-${dc.id}`} className="label-caps hover:text-foreground mr-3">Edit</button>
                            <button onClick={() => del(dc.id)} className="label-caps hover:text-destructive">Del</button>
                          </>
                        )}
                      </td>
                    </tr>
                    {openDcId === dc.id && (
                      <tr>
                        <td colSpan={8} className="bg-secondary p-0">
                          <div className="p-3">
                            <div className="label-caps mb-2">Partial Receive · enter exact received {meta.unit} per line</div>
                            <table className="table-dense w-full bg-white">
                              <thead>
                                <tr>
                                  {meta.needsProduct && <th>Product</th>}
                                  <th>Lot #</th>
                                  <th className="text-right">Sent {meta.unit}</th>
                                  {isAdmin && <th className="text-right">Rate ₹</th>}
                                  {stage === "stitching" ? (
                                    <>
                                      <th className="text-right">Boxes</th>
                                      <th className="text-right">Loose Pcs</th>
                                      <th className="text-right">Mistake Pcs</th>
                                      <th className="text-right">Total Pcs</th>
                                    </>
                                  ) : (
                                    <th className="text-right">Received {meta.unit}</th>
                                  )}
                                  {isAdmin && <th className="text-right">Bill</th>}
                                  <th>Status</th>
                                  <th>Notes</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(dc.lines || []).map((l) => {
                                  const isStitch = stage === "stitching";
                                  const draft = isStitch ? (receiveDraft[l.id] || {}) : {};
                                  const ppb = (products.find((p) => p.id === l.product_id) || {}).pieces_per_box || 0;
                                  const boxes = Number(draft.boxes_received ?? l.boxes_received ?? 0) || 0;
                                  const loose = Number(draft.loose_pcs_received ?? l.loose_pcs_received ?? 0) || 0;
                                  const mistake = Number(draft.mistake_pcs ?? l.mistake_pcs ?? 0) || 0;
                                  const legacyDraft = Number(receiveDraft[l.id] || 0);
                                  const goodPcs = isStitch ? (boxes * ppb + loose + mistake) : legacyDraft;
                                  const sentUnits = Number(l.qty_sent);
                                  // For stitching, use exact hybrid pcs (qty_sent_pcs) when present so
                                  // the "expected return" cap and display are always whole numbers,
                                  // never decimals like 181.32 from 15.11 doz × 12.
                                  const sentPcsExact = Number(l.qty_sent_pcs) > 0
                                    ? parseInt(l.qty_sent_pcs, 10)
                                    : Math.round(sentUnits * 12);
                                  const capForStatus = isStitch ? sentPcsExact : sentUnits;
                                  const s = goodPcs <= 0 ? "pending" : goodPcs < capForStatus ? "partial" : "received";
                                  const setStitchField = (field, val) => setReceiveDraft({
                                    ...receiveDraft,
                                    [l.id]: { ...(receiveDraft[l.id] || {}), [field]: val },
                                  });
                                  return (
                                    <tr key={l.id}>
                                      {meta.needsProduct && <td>{l.product_label || "—"}</td>}
                                      <td className="font-mono text-xs">{l.lot_no ? (
                                        <Link to={`/lots/${encodeURIComponent(l.lot_no)}`} className="hover:underline text-accent">{l.lot_no}</Link>
                                      ) : <span className="text-muted-foreground">—</span>}</td>
                                      <td className="tabular text-right">{l.qty_sent}{isStitch && <span className="text-muted-foreground text-xs"> ({sentPcsExact}pcs)</span>}</td>
                                      {isAdmin && <td className="tabular text-right">{l.rate}</td>}
                                      {isStitch ? (
                                        <>
                                          <td className="text-right">
                                            <input type="number" min="0" data-testid={`dc-recv-boxes-${l.id}`}
                                              value={draft.boxes_received ?? l.boxes_received ?? ""}
                                              onChange={(e) => setStitchField("boxes_received", e.target.value)}
                                              className="h-8 px-2 border border-border bg-white text-right font-mono text-xs w-16" />
                                            {ppb > 0 && <div className="text-[9px] text-muted-foreground">×{ppb}={boxes * ppb}</div>}
                                          </td>
                                          <td className="text-right">
                                            <input type="number" min="0" data-testid={`dc-recv-loose-${l.id}`}
                                              value={draft.loose_pcs_received ?? l.loose_pcs_received ?? ""}
                                              onChange={(e) => setStitchField("loose_pcs_received", e.target.value)}
                                              className="h-8 px-2 border border-border bg-white text-right font-mono text-xs w-16" />
                                          </td>
                                          <td className="text-right">
                                            <input type="number" min="0" data-testid={`dc-recv-mistake-${l.id}`}
                                              value={draft.mistake_pcs ?? l.mistake_pcs ?? ""}
                                              onChange={(e) => setStitchField("mistake_pcs", e.target.value)}
                                              className="h-8 px-2 border border-border bg-white text-right font-mono text-xs w-16" />
                                          </td>
                                          <td className="tabular text-right font-bold">{goodPcs}</td>
                                        </>
                                      ) : (
                                        <td className="text-right">
                                          <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max={l.qty_sent}
                                            inputMode="decimal"
                                            data-testid={`dc-recv-${l.id}`}
                                            value={receiveDraft[l.id] ?? ""}
                                            onChange={(e) => setReceiveDraft({ ...receiveDraft, [l.id]: e.target.value })}
                                            className="h-8 px-2 border border-border bg-white text-right font-mono text-xs w-24"
                                          />
                                        </td>
                                      )}
                                      {isAdmin && <td className="tabular text-right">{fmtINR(goodPcs * Number(l.rate || 0))}</td>}
                                      <td><span className={`inline-block label-caps px-2 py-0.5 border ${STATUS_STYLE[s]}`}>{s}</span></td>
                                      <td className="text-muted-foreground text-xs">{l.notes}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            <div className="mt-3 flex gap-2">
                              <button type="button" onClick={() => submitReceive(dc)} data-testid={`dc-save-recv-${dc.id}`} className="h-9 px-4 bg-foreground text-background font-mono text-[11px] tracking-[0.15em] uppercase font-bold">
                                Save Receive
                              </button>
                              <button type="button" onClick={() => setOpenDcId(null)} className="h-9 px-4 border border-border font-mono text-[11px] tracking-[0.15em] uppercase font-bold">
                                Cancel
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {filteredList.length === 0 && (
                  <tr><td colSpan={8}><Empty text={q ? "No DCs match your search" : `No ${meta.title} DCs yet`} /></td></tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Mobile: card-per-DC. Receive drawer moves inline so no horizontal scroll. */}
          <div className="md:hidden space-y-3">
            {filteredList.map((dc) => {
              const totSent = (dc.lines || []).reduce((a, l) => a + Number(l.qty_sent || 0), 0);
              const totRecv = (dc.lines || []).reduce((a, l) => a + Number(l.qty_received || 0), 0);
              const lots = [...new Set((dc.lines || []).map((l) => l.lot_no).filter(Boolean))];
              return (
                <div key={dc.id} className="border border-border bg-white p-3" data-testid={`dc-card-${dc.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-mono font-semibold text-sm">{dc.dc_number}</div>
                      {dc.is_auto && <span className="text-[9px] tracking-widest font-bold text-accent">AUTO</span>}
                      <div className="text-[10px] text-muted-foreground font-mono">{dc.date}</div>
                      <div className="text-sm mt-0.5 truncate">{dc.party_name}</div>
                      {lots.length > 0 && <div className="text-[10px] text-muted-foreground mt-0.5">Lot {lots.slice(0,3).join(", ")}{lots.length > 3 ? ` +${lots.length-3}` : ""}</div>}
                    </div>
                    <span className={`shrink-0 inline-block label-caps px-2 py-0.5 border text-[9px] ${STATUS_STYLE[dc.status] || "border-border"}`}>{dc.status}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <div>
                      <div className="label-caps text-muted-foreground text-[9px]">Sent {meta.unit}</div>
                      <div className="tabular text-sm font-bold">{totSent.toFixed(2).replace(/\.00$/, "")}</div>
                    </div>
                    <div>
                      <div className="label-caps text-muted-foreground text-[9px]">Recv {stage === "stitching" ? "Pcs" : meta.unit}</div>
                      <div className="tabular text-sm font-bold">{totRecv.toFixed(2).replace(/\.00$/, "")}</div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-border flex gap-2 flex-wrap">
                    <button onClick={() => toggleOpen(dc)} data-testid={`dc-open-mobile-${dc.id}`} className="flex-1 h-9 border border-border font-mono text-[10px] tracking-[0.15em] uppercase font-bold min-w-[70px]">
                      {openDcId === dc.id ? "Close" : "Receive"}
                    </button>
                    {dc.status !== "received" && (
                      <button onClick={() => markFullyReceived(dc)} data-testid={`dc-mark-received-mobile-${dc.id}`} className="flex-1 h-9 border border-green-700 text-green-700 font-mono text-[10px] tracking-[0.15em] uppercase font-bold min-w-[70px]">
                        ✓ Fully
                      </button>
                    )}
                    <button onClick={() => printDC(dc)} data-testid={`dc-print-mobile-${dc.id}`} className="flex-1 h-9 border border-border font-mono text-[10px] tracking-[0.15em] uppercase font-bold min-w-[70px]">Print</button>
                    {isAdmin && (
                      <button onClick={() => del(dc.id)} data-testid={`dc-del-mobile-${dc.id}`} className="h-9 px-3 border border-destructive text-destructive font-mono text-[10px] tracking-[0.15em] uppercase font-bold">Del</button>
                    )}
                  </div>
                  {openDcId === dc.id && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <div className="label-caps mb-2">Receive Lines</div>
                      <div className="space-y-2">
                        {(dc.lines || []).map((l) => (
                          <div key={l.id} className="border border-border bg-secondary/30 p-2">
                            <div className="text-xs font-semibold truncate">{l.product_label || l.lot_no || "—"}</div>
                            <div className="text-[10px] text-muted-foreground font-mono">
                              Sent {Number(l.qty_sent).toFixed(2).replace(/\.00$/, "")} {meta.unit}
                              {stage === "stitching" && (() => {
                                // Exact pcs from hybrid qty_sent_pcs (falls back to legacy qty_sent*12).
                                const pcs = Number(l.qty_sent_pcs) > 0
                                  ? parseInt(l.qty_sent_pcs, 10)
                                  : Math.round(Number(l.qty_sent || 0) * 12);
                                return <> · <b>{pcs}</b> pcs</>;
                              })()}
                            </div>
                            <div className="mt-2">
                              {stage === "stitching" ? (
                                <div className="grid grid-cols-3 gap-1 text-[10px]">
                                  <input type="number" min="0" placeholder="Bx" inputMode="numeric" value={receiveDraft[l.id]?.boxes ?? ""} onChange={(e) => setReceiveDraft({ ...receiveDraft, [l.id]: { ...(receiveDraft[l.id] || {}), boxes: e.target.value } })} className="h-9 px-2 border border-border bg-white font-mono text-xs" />
                                  <input type="number" min="0" placeholder="Loose" inputMode="numeric" value={receiveDraft[l.id]?.loose ?? ""} onChange={(e) => setReceiveDraft({ ...receiveDraft, [l.id]: { ...(receiveDraft[l.id] || {}), loose: e.target.value } })} className="h-9 px-2 border border-border bg-white font-mono text-xs" />
                                  <input type="number" min="0" placeholder="Mistake" inputMode="numeric" value={receiveDraft[l.id]?.mistake ?? ""} onChange={(e) => setReceiveDraft({ ...receiveDraft, [l.id]: { ...(receiveDraft[l.id] || {}), mistake: e.target.value } })} className="h-9 px-2 border border-border bg-white font-mono text-xs" />
                                </div>
                              ) : (
                                <input type="number" step="0.01" min="0" inputMode="decimal" placeholder="Received qty" value={receiveDraft[l.id] ?? ""} onChange={(e) => setReceiveDraft({ ...receiveDraft, [l.id]: e.target.value })} className="w-full h-9 px-2 border border-border bg-white font-mono text-xs" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <button onClick={() => submitReceive(dc)} className="w-full mt-3 h-10 bg-foreground text-background font-mono text-[10px] tracking-[0.15em] uppercase font-bold">Save Receive</button>
                    </div>
                  )}
                </div>
              );
            })}
            {filteredList.length === 0 && <Empty text={q ? "No DCs match your search" : `No ${meta.title} DCs yet`} />}
          </div>
        </Card>
      </div>
      {nextPrompt && (
        <NextStagePrompt
          dc={nextPrompt.dc}
          parties={parties}
          defaultStage={nextPrompt.defaultStage}
          defaultPartyId={nextPrompt.defaultPartyId}
          onClose={() => setNextPrompt(null)}
          onConfirm={(stage, partyId) => createNextDc({ dcId: nextPrompt.dc.id, stage, partyId })}
        />
      )}
    </div>
  );
}

function NextStagePrompt({ dc, parties, defaultStage, defaultPartyId, onClose, onConfirm }) {
  const options = NEXT_STAGE_MAP[dc.stage] || [];
  const [stage, setStage] = useState(defaultStage || options[0]?.value || "");
  const [partyId, setPartyId] = useState(defaultPartyId || "");
  const active = options.find((o) => o.value === stage);
  const filteredParties = parties.filter((p) => p.type === active?.partyType);
  const submit = (e) => {
    e.preventDefault();
    if (!stage) return toast.error("Pick a next stage");
    if (!partyId) return toast.error("Pick a vendor");
    onConfirm(stage, partyId);
  };
  const lots = [...new Set((dc.lines || []).map((l) => l.lot_no).filter(Boolean))];
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-background border-2 border-border w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="dc-next-prompt">
        <div>
          <div className="label-caps text-accent">Forward to Next Stage</div>
          <div className="font-display font-black text-xl mt-1">Received: {dc.total_received} kg</div>
          <div className="text-xs font-mono text-muted-foreground mt-1">
            From {dc.dc_number} · {dc.party_name}
            {lots.length > 0 && <> · Lot {lots.join(", ")}</>}
          </div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Next Stage">
            <Select required value={stage} onChange={(e) => { setStage(e.target.value); setPartyId(""); }} data-testid="next-prompt-stage">
              <option value="">— skip —</option>
              {options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </Select>
          </Field>
          <Field label={active ? `${active.label} · Vendor` : "Vendor"}>
            <Combobox
              testid="next-prompt-party"
              required
              value={partyId}
              onChange={setPartyId}
              disabled={!stage}
              placeholder={stage ? "Type to search vendor…" : "Pick a stage first"}
              options={filteredParties.map((p) => ({ value: p.id, label: p.name, hint: p.phone || "" }))}
            />
          </Field>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-10 label-caps border border-border hover:bg-secondary" data-testid="next-prompt-skip">Skip</button>
            <Btn data-testid="next-prompt-create">Create Next DC</Btn>
          </div>
        </form>
      </div>
    </div>
  );
}
